import React, { useState, useEffect } from "react";
import { getCachedWorkers } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import {
  useDisponibiliteStore,
  AvailabilityBlock,
} from "@store/disponibiliteStore";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import SearchField from "@components/ui/textfields/SearchField";
import MiniCalendar from "@components/ui/MiniCalendar";
import { WeekTimeGrid, BLOCK_COLORS } from "./WeekTimeGrid";
import { DayTimeGrid } from "./DayTimeGrid";
import { BlockModal } from "./BlockModal";

export function Disponibilites() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchBlocks = useDisponibiliteStore((s) => s.fetchBlocks);
  useEffect(() => {
    if (orgData?.id) fetchBlocks(orgData.id);
  }, [orgData?.id]);

  const selectedDate = useDisponibiliteStore((s) => s.selectedDate);
  const setSelectedDate = useDisponibiliteStore((s) => s.setSelectedDate);
  const viewMode = useDisponibiliteStore((s) => s.viewMode);
  const setViewMode = useDisponibiliteStore((s) => s.setViewMode);
  const selectedWorkerId = useDisponibiliteStore(
    (s) => s.selectedWorkerId,
  );
  const setSelectedWorkerId = useDisponibiliteStore(
    (s) => s.setSelectedWorkerId,
  );
  const searchQuery = useDisponibiliteStore((s) => s.searchQuery);
  const setSearchQuery = useDisponibiliteStore((s) => s.setSearchQuery);
  const selectedWorkerIds = useDisponibiliteStore(
    (s) => s.selectedWorkerIds,
  );
  const toggleWorkerSelection = useDisponibiliteStore(
    (s) => s.toggleWorkerSelection,
  );
  const selectAllWorkers = useDisponibiliteStore(
    (s) => s.selectAllWorkers,
  );
  const deselectAllWorkers = useDisponibiliteStore(
    (s) => s.deselectAllWorkers,
  );
  const getRecurringSummary = useDisponibiliteStore(
    (s) => s.getRecurringSummary,
  );
  const getCongesSummary = useDisponibiliteStore(
    (s) => s.getCongesSummary,
  );

  const [editingBlock, setEditingBlock] =
    useState<AvailabilityBlock | null>(null);

  const recurrents = getRecurringSummary(selectedWorkerId);
  const conges = getCongesSummary(selectedWorkerId);

  return (
    <div
      className="flex flex-col h-screen overflow-auto"
      style={{ background: "#FAFAFA" }}
    >
      <div className="flex flex-col h-fit">
        {/* Header */}
        <div
          className="flex flex-col"
          style={{ gap: 4, padding: "30px 40px 0 40px" }}
        >
          <span
            style={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: 20,
              lineHeight: "30px",
              color: "#000",
            }}
          >
            Disponibilités
          </span>
          <span
            className="text-sm font-medium"
            style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
          >
            Gérez les disponibilités de vos équipes ici.
          </span>
          <span
            className="text-h4 font-medium"
            style={{
              fontFamily: "Poppins",
              color: "#A1A1A1",
              marginTop: 2,
            }}
          >
            Ajoutez, modifiez et supprimez les membres de votre équipe pour
            contrôler l'accès et les permissions au sein de votre
            organisation.
          </span>
        </div>

        {/* Worker tabs / filter pills */}
        <div
          className="flex flex-row flex-wrap gap-1 items-center"
          style={{ padding: "16px 40px 0 40px" }}
        >
          {viewMode === "semaine" ? (
            getCachedWorkers().map((w) => (
              <button
                key={w.id}
                onClick={() => setSelectedWorkerId(w.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedWorkerId === w.id
                    ? "bg-white text-black shadow border border-neutral-200"
                    : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
                }`}
                style={{ fontFamily: "Poppins" }}
              >
                {w.name}
              </button>
            ))
          ) : (
            <>
              <button
                onClick={() =>
                  selectedWorkerIds.length === getCachedWorkers().length
                    ? deselectAllWorkers()
                    : selectAllWorkers()
                }
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border ${
                  selectedWorkerIds.length === getCachedWorkers().length
                    ? "bg-neutral-800 text-white border-neutral-800"
                    : "bg-white text-neutral-500 border-neutral-200 hover:bg-neutral-50"
                }`}
                style={{ fontFamily: "Poppins" }}
              >
                Tous
              </button>
              {getCachedWorkers().map((w) => {
                const isSelected = selectedWorkerIds.includes(w.id);
                return (
                  <button
                    key={w.id}
                    onClick={() => toggleWorkerSelection(w.id)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border ${
                      isSelected
                        ? "bg-white text-black shadow border-neutral-200"
                        : "text-neutral-400 border-transparent hover:text-neutral-600 hover:bg-neutral-100"
                    }`}
                    style={{ fontFamily: "Poppins" }}
                  >
                    {isSelected && (
                      <span style={{ marginRight: 4, color: "#6C5CE7" }}>
                        &#10003;
                      </span>
                    )}
                    {w.name}
                  </button>
                );
              })}
            </>
          )}
        </div>

        {/* Body */}
        <div
          className="flex flex-row flex-1"
          style={{
            gap: 24,
            padding: "16px 30px 0 30px",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Left sidebar */}
          <div
            className="flex flex-col flex-shrink-0"
            style={{
              width: 196,
              gap: 20,
              overflowY: "auto",
              paddingBottom: 30,
            }}
          >
            <MiniCalendar
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              weekHighlight={viewMode === "semaine"}
              showNavButtons
            />

            {/* Recurring summary (week view only) */}
            {viewMode === "semaine" && recurrents.length > 0 && (
              <div className="flex flex-col" style={{ gap: 6 }}>
                <span
                  className="text-h4 font-medium"
                  style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
                >
                  Récurrents
                </span>
                {recurrents.map((r, i) => (
                  <div
                    key={i}
                    className="flex flex-row justify-between items-center py-1.5 px-2 rounded-lg"
                    style={{
                      background: "#fff",
                      border: "1px solid #F0F0F0",
                      boxShadow:
                        "0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -2px rgba(0,0,0,0.1)",
                    }}
                  >
                    <span
                      className="text-h5 font-medium"
                      style={{ color: "#525252" }}
                    >
                      {r.label}
                    </span>
                    <span
                      className="text-h5 font-medium"
                      style={{ color: "#A1A1A1" }}
                    >
                      ({r.totalHours.toFixed(2)})
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Congés summary (week view only) */}
            {viewMode === "semaine" && conges.length > 0 && (
              <div className="flex flex-col" style={{ gap: 6 }}>
                <span
                  className="text-h4 font-medium"
                  style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
                >
                  Congés posés
                </span>
                {conges.map((c, i) => (
                  <div
                    key={i}
                    className="flex flex-row justify-between items-center py-1.5 px-2 rounded-lg"
                    style={{
                      background: "#fff",
                      border: "1px solid #F0F0F0",
                      boxShadow:
                        "0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -2px rgba(0,0,0,0.1)",
                    }}
                  >
                    <span
                      className="text-h5 font-medium"
                      style={{ color: "#525252" }}
                    >
                      {c.label}
                    </span>
                    <span
                      className="text-h5 font-medium"
                      style={{ color: "#A1A1A1" }}
                    >
                      ({c.totalHours.toFixed(2)})
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Color legend */}
            <div className="flex flex-col" style={{ gap: 8 }}>
              <span
                className="text-h4 font-medium"
                style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
              >
                Légende
              </span>
              {(
                [
                  { key: "recurring", label: "Récurrent" },
                  { key: "punctual", label: "Ponctuel" },
                ] as const
              ).map((item) => (
                <div
                  key={item.key}
                  className="flex flex-row items-center"
                  style={{ gap: 8 }}
                >
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      background: BLOCK_COLORS[item.key].bg,
                      border: `1px solid ${BLOCK_COLORS[item.key].border}`,
                    }}
                  />
                  <span
                    className="text-h4 font-medium"
                    style={{
                      fontFamily: "Poppins",
                      color: "#737373",
                    }}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right content */}
          <div
            className="flex flex-col flex-1 overflow-hidden"
            style={{ gap: 10, minWidth: 0 }}
          >
            {/* Search + view toggle */}
            <div
              className="flex flex-row items-center"
              style={{ gap: 16 }}
            >
              <div style={{ width: 220 }}>
                <SearchField
                  value={searchQuery}
                  onChange={(v: string) => setSearchQuery(v)}
                  placeholder="Chercher .."
                />
              </div>
              <div style={{ width: 180 }}>
                <ChunkyTabs
                  activeTab={viewMode}
                  tabs={[
                    { value: "jour", label: "Jour" },
                    { value: "semaine", label: "Semaine" },
                  ]}
                  setActiveTab={(tab) =>
                    setViewMode(tab as "jour" | "semaine")
                  }
                />
              </div>
            </div>

            {/* Time grid */}
            <div
              className="flex flex-col flex-1 overflow-hidden rounded-lg"
              style={{
                border: "1px solid #E5E5E5",
                background: "#fff",
              }}
            >
              {viewMode === "semaine" ? (
                <WeekTimeGrid onEditBlock={setEditingBlock} />
              ) : (
                <DayTimeGrid onEditBlock={setEditingBlock} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {editingBlock && (
        <BlockModal
          mode="edit"
          block={editingBlock}
          onClose={() => setEditingBlock(null)}
        />
      )}
    </div>
  );
}
